import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import ConsentField from '../../components/ConsentField/ConsentField';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { EventApplicationSubmitted } from '../../helpers/gtm';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSetRightPanelContent from '../../hooks/useSetRightPanelContent';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { getApplicationState, submitCoApplicant } from '../../store/application/application';
import { addOrUpdateFormField, invalidateForm } from '../../store/applicationform';
import { selectGovernmentIdPageIndex, setCurrentPage, WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';
import CoApplicantSubmitPageSidePanelDK from './CoApplicantSubmitPageSidePanelDK';

// Fallback wizard index of CoApplicantEskatKreditStatusConsentPageDK (the government-id page) in the DK
// co-applicant wizard. Only used if that page never recorded its actual position (it always does before this
// page is reachable). See selectGovernmentIdPageIndex / setGovernmentIdPageIndex (LP-2980, mirrors
// SubmitPageDK's ESKAT_KREDITSTATUS_PAGE_FALLBACK_INDEX). Exported so tests assert against the same value
// rather than duplicating the literal.
export const COAPPLICANT_ESKAT_KREDITSTATUS_PAGE_FALLBACK_INDEX = 1;

const CoApplicantSubmitPageDK = () => {
    useComponentTracker('CoApplicantSubmitPageDK');
    const translate = useTranslation();
    const theme = useTheme();
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { coApplicantTotalSections } = usePagesConfig();
    const dispatch = useAppDispatch();
    const { currentPage } = usePagesContext();
    const applicationId = useAppSelector((state) => state.application.applicationId);
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const { consentDetails, isError } = useGetConsentService('TERMS_LOANS');
    const defaultPagesAction = useDefaultPagesAction();
    const applicationReferenceNumber = useArnFromPath();
    const { countryCode } = useSettings();
    const governmentIdPageIndex = useAppSelector(selectGovernmentIdPageIndex);

    useSetBannerType('none');

    const sidePanelContent = useMemo(() => <CoApplicantSubmitPageSidePanelDK />, []);
    useSetRightPanelContent(greaterThanLg ? sidePanelContent : null);

    const requiredFieldConsents =
        consentDetails
            ?.filter((consentDetails) => consentDetails.required)
            .map((consentDetails) => consentDetails.name) ?? [];

    // Validate the required consents plus coapplicantGovernmentId (collected earlier, on
    // CoApplicantEskatKreditStatusConsentPageDK), for symmetry with SubmitPageDK's applicantGovernmentId gate
    // (LP-2980).
    const fieldsToValidate = [...requiredFieldConsents, AttributeNames.coapplicantGovernmentId];
    const validationErrors = useValidation(fieldsToValidate);

    useEffect(() => {
        setErrorOccurred(false);
    }, []);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'submit-coapplicant') {
            if (validationErrors.length) {
                const governmentIdMissing = validationErrors.some(
                    (validationError) => validationError.fieldName === AttributeNames.coapplicantGovernmentId,
                );
                // Persist every other error so the affected fields render their error state. Deliberately
                // excluding the government-id one: there is no rendered input for it here to show an error
                // on, and CoApplicantEskatKreditStatusConsentPageDK's own extraction effect only ever
                // dispatches `value` (never `hasError`) -- so persisting a synthetic hasError: true for it
                // would survive a later successful MitID retry untouched (addUpdateFormFieldArray only
                // overwrites keys present in the new payload) and permanently re-trigger this same
                // routing-back branch on every future submit attempt.
                validationErrors
                    .filter((validationError) => validationError.fieldName !== AttributeNames.coapplicantGovernmentId)
                    .forEach((validationError) => {
                        dispatch(addOrUpdateFormField(validationError));
                    });

                if (governmentIdMissing) {
                    // The government-id field is collected on CoApplicantEskatKreditStatusConsentPageDK, not
                    // on this page. Rather than silently submitting an empty value, route the co-applicant
                    // back to retry the MitID login, using the index that page recorded for the active wizard.
                    dispatch(
                        setCurrentPage(governmentIdPageIndex ?? COAPPLICANT_ESKAT_KREDITSTATUS_PAGE_FALLBACK_INDEX),
                    );
                } else {
                    scrollToFieldWithError(validationErrors);
                }
                return false;
            }

            try {
                await dispatch(submitCoApplicant({ applicationReferenceNumber: applicationReferenceNumber! })).unwrap();
                dispatch(invalidateForm());
                await dispatch(getApplicationState({ applicationReferenceNumber }));
                dispatch(setCurrentPage(0));
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            } finally {
                EventApplicationSubmitted(applicationId, countryCode, 'coapplicant');
            }
            return false;
        } else {
            return defaultPagesAction(action);
        }
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={2}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.about-the-loan')}
            />
            <Title text={translate('page.submit.title')} />

            {!greaterThanLg && <CoApplicantSubmitPageSidePanelDK />}

            <Subtitle text={translate('page.submit.subtitle.consent')} />
            {consentDetails?.map((consentDetail) => (
                <ConsentField key={consentDetail.name} consentDetail={consentDetail} />
            ))}
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.submit.error.title`)}
                    description={translate(`page.submit.error.description`)}
                />
            )}
            {isError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['submit-coapplicant']}
                onAction={onAction}
                currentSection={2}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={1}
                currentPageInSection={1}
                sectionTitle={translate('field.section.about-the-loan')}
            />
        </>
    );
};

export default CoApplicantSubmitPageDK;
